<template>
    <base-dialog :loading="loading" :value="dialog" :width="640" :fullscreen="isMobile" :title="$t('routines.copy_routine')"
        @input="val => { if (!val) hide() }" @close="hide">
        <v-row dense>
                    <v-col cols="12" md="6" v-if="!autoDate">
                        <kimsa-combobox :label="$t('routines.date_from_copy')"
                            :placeholder="`01-01-${new Date().getFullYear()}`" dense :items="filteredDates"
                            @change="selectDate = $event" item-text="date" item-value="assignedRoutineId" ref="dates" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kimsa-combobox :label="$t('common.copy_to')" dense :items="copyToOptions"
                            :value="copyTo" @change="copyTo = $event" no-object ref="copyTo" />
                    </v-col>
                    <v-col cols="12" md="6" v-if="!multiple">
                        <kimsa-date-picker :label="$t('routines.date_to_copy')" solo flat dense color="secondary"
                            @change="startDate = $event" :allowed-dates="datesAllowed" ref="start" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kimsa-combobox :label="$t('routines.destination_routine')" dense :items="routineOptions"
                            :value="routineDestiny" @change="routineDestiny = $event" no-object ref="routineDestiny" />
                    </v-col>
                    <v-col cols="12">
                        <div class="toggle-row">
                            <div class="toggle-row-text">
                                <span class="label toggle-label" @click="multiple = !multiple">{{ $t('routines.multiple') }}</span>
                                <span class="toggle-hint">{{ $t('routines.multiple_hint') }}</span>
                            </div>
                            <kimsa-toggle :value="multiple" @change="multiple = $event" />
                        </div>
                    </v-col>
                    <template v-if="multiple">
                        <v-col cols="12" md="6">
                            <kimsa-date-picker :label="$t('routines.from')" solo flat dense color="secondary"
                                :max="endDate" @change="startDate = $event" ref="start" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <kimsa-date-picker :disabled="!startDate" :label="$t('routines.to')" solo flat dense
                                color="secondary" :min="startDate" @change="endDate = $event" ref="end" />
                        </v-col>
                        <v-col cols="12">
                            <div class="label mb-2">{{ $t('routines.repeat_days') }}</div>
                            <div class="day-pills">
                                <button type="button" v-for="(day, i) in days" :key="i" class="day-pill"
                                    :class="{ active: day.active }" @click="days[i].active = !days[i].active">
                                    {{ day.name }}
                                </button>
                            </div>
                            <div v-if="emptyRange" class="text-caption error--text mt-2">{{ $t('routines.no_days_in_range') }}</div>
                        </v-col>
                    </template>
                    <template v-if="copyTo !== 'self'">
                        <v-col cols="12">
                            <kimsa-combobox searchable :label="labelOptions"
                                :placeholder="$t('common.select_placeholder')" dense :items="optionsFiltered"
                                @change="onChangeSetOption($event)" :value="currentValues" item-value="objectID"
                                :multiple-with-chips="copyTo !== 'teams'" ref="options" />
                        </v-col>
                        <v-col cols="12" v-if="copyTo === 'teams'">
                            <kimsa-combobox searchable :label="$t('common.subgroups')"
                                :placeholder="$t('common.select_placeholder')" dense
                                :items="subgroupsTeamOptions" @change="setCurrentTeamGroup($event)" :value="subgroups"
                                :readonly="!team" multiple-with-chips ref="subgroup" />
                        </v-col>
                    </template>
        </v-row>

        <select-replace-assigned-routines-dialog :assigned-routines-in-same-date="assignedRoutinesInSameDate" mode="dates"
            persistent is-second-dialog @confirm="onConfirmReplace($event)" @cancel="loading = false"
            ref="replaceRoutinesDates" />

        <template v-slot:footer>
            <dialog-actions :cancel-label="$t('routines.reset')" :confirm-label="$t('routines.copy')"
                :disabled="disabled" :loading="loading" @cancel="reset" @confirm="checkAssignRoutines" />
        </template>
    </base-dialog>
</template>

<script>

import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import { db } from "@/utils/Firebase";
import {
    addAssignedRoutinesGroup, clearBlocksAssignedRoutine, getBlockNextPrescription,
    deleteAssignedRoutinesGroup, deleteAssignedRoutineStudentInDate, getPayloadBaseAssignedRoutine,
    hasAssignedRoutineInDate,
    hasGroupAssignedRoutineInDate
} from "@/utils/Exercises";
import SelectReplaceAssignedRoutinesDialog
    from "@/components/coach/routines/SelectReplaceAssignedRoutinesDialog";
import { PrescriptionIndex } from "@/utils/Algolia";
import { reportDiagnostic } from "@/utils/ErrorReporter";
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import KimsaToggle from "@/components/common/inputs/KimsaToggle";

export default {
    name: "CopyRoutineDialog",
    components: { SelectReplaceAssignedRoutinesDialog, KimsaDatePicker, KimsaCombobox, BaseDialog, DialogActions, KimsaToggle },
    props: {
        beforeCopy: {
            type: Function,
            default: null,
        },
        studentsOptions: {
            type: Array,
            default: () => [],
        },
        groupsOptions: {
            type: Array,
            default: () => [],
        },
        teamsOptions: {
            type: Array,
            default: () => [],
        },
        currentSelection: {
            type: Object,
            default: null,
        },
        dates: {
            type: Array,
            default: () => [],
        },
        coachId: {
            type: String,
            default: null
        },
        routineNumber: {
            type: Number,
            default: 1,
        },
        autoDate: {
            type: Boolean,
            default: false,
        }
    },
    data(vm) {
        return {
            dialog: false,
            selectDate: null,
            multiple: false,
            copyToOptions: [
                { name: vm.$t('common.self'), value: 'self' },
                { name: vm.$t('common.other_students'), value: 'students' },
                { name: vm.$t('common.other_groups'), value: 'groups' },
                { name: vm.$t('common.other_teams'), value: 'teams' },
            ],
            copyTo: 'self',
            students: [],
            groups: [],
            team: null,
            subgroups: [],
            startDate: '',
            endDate: '',
            loading: false,
            days: [
                { name: vm.$t('common.short_monday'), value: 1, active: false },
                { name: vm.$t('common.short_tuesday'), value: 2, active: false },
                { name: vm.$t('common.short_wednesday'), value: 3, active: false },
                { name: vm.$t('common.short_thursday'), value: 4, active: false },
                { name: vm.$t('common.short_friday'), value: 5, active: false },
                { name: vm.$t('common.short_saturday'), value: 6, active: false },
                { name: vm.$t('common.short_sunday'), value: 0, active: false },
            ],
            assignedRoutinesInSameDate: [],
            datesToSkip: [],
            operationId: 0,
            routineDestiny: this.routineNumber || 1,
        }
    },
    computed: {
        disabled(vm = this) {
            if ((!vm.selectDate && !vm.autoDate) || !vm.startDate) return true
            if (vm.multiple && (!vm.endDate || vm.days.filter(day => day.active).length === 0)) return true
            if (vm.emptyRange) return true
            if (vm.copyTo !== 'self' && (!vm.currentValues || vm.currentValues.length === 0)) return true
            if (vm.copyTo === 'teams' && vm.subgroups.length === 0) return true
            if (vm.destinyToCopy.length === 0) return true
            return false
        },
        emptyRange(vm = this) {
            return vm.multiple && !!vm.startDate && !!vm.endDate && vm.days.some(day => day.active) && vm.daysToCopy().length === 0
        },
        currentValues(vm = this) {
            if (vm.copyTo === 'teams') return vm.team
            return vm[vm.copyTo]
        },
        optionsFiltered(vm = this) {
            if (vm.copyTo === 'groups') return vm.groupsOptionsFiltered
            if (vm.copyTo === 'teams') return vm.teamsOptions
            return vm.studentsOptionsFiltered
        },
        studentsOptionsFiltered(vm = this) {
            return vm.studentsOptions?.filter(student => student.objectID !== vm.currentSelection?.objectID) || []
        },
        groupsOptionsFiltered(vm = this) {
            return vm.groupsOptions?.filter(group => group.id !== vm.currentSelection?.id) || []
        },
        subgroupsTeamOptions(vm = this) {
            const groups = (vm.team?.groups || []).map((subgroup, i) => ({
                ...subgroup,
                name: subgroup.name || `${vm.$t('common.subgroup')} ${i + 1}`
            }))
            if (vm.team?.id === vm.currentSelection?.teamId)
                return groups.filter(subgroup => subgroup.id !== vm.currentSelection.id || null)
            return groups
        },
        groupTeamOptionsFiltered(vm = this) {
            return vm.groupsOptions?.filter(group => group.id !== vm.currentSelection.id || null) || []
        },
        labelOptions(vm = this) {
            return vm.$t(`common.${vm.copyTo}`)
        },
        currentSelectionType(vm = this) {
            if (!!vm.currentSelection?.teamId) return 'team'
            if (!!vm.currentSelection?.students) return 'group'
            return 'student'
        },
        destinyToCopy(vm = this) {
            if (vm.copyTo === 'self') return [vm.currentSelection].filter(Boolean)
            if (vm.copyTo === 'students') return (vm.students || []).filter(Boolean)
            if (vm.copyTo === 'groups') return (vm.groups || []).filter(Boolean)
            if (vm.copyTo === 'teams') return (vm.subgroups || []).filter(Boolean).map(sg => ({ teamId: vm.team.id, ...sg }))
            return []
        },
        isGroup(vm = this) {
            return (vm.copyTo === 'self' && vm.currentSelectionType !== 'student') || (vm.copyTo === 'groups' || vm.copyTo === 'teams')
        },
        collectionName(vm = this) {
            return vm.currentSelectionType === 'student' ? 'assignedRoutines' : 'groupAssignedRoutines'
        },
        routineNumberDestiny(vm = this) {
            return vm.routineDestiny || 1
        },
        routineOptions(vm = this) {
            return [1, 2].map(value => ({ name: `${vm.$t('common.routine')} ${value}`, value }))
        },
        filteredDates(vm = this) {
            return vm.dates.filter(date => date.routineNumber === vm.routineNumberDestiny)
        }
    },
    methods: {
        show(date = null) {
            const vm = this
            vm.dialog = true
            vm.setDate(date)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.operationId++
            vm.loading = false
            vm.selectDate = null
            vm.multiple = false
            vm.copyTo = 'self'
            vm.students = []
            vm.groups = []
            vm.team = null
            vm.subgroups = []
            vm.startDate = ''
            vm.endDate = ''
            vm.routineDestiny = vm.routineNumber || 1
            vm.days.forEach(day => { day.active = false })
            vm.$nextTick(() => {
                if (vm.$refs.copyTo) vm.$refs.copyTo.model = 'self'
                if (vm.$refs.routineDestiny) vm.$refs.routineDestiny.model = vm.routineDestiny
                if (vm.$refs.dates) vm.$refs.dates.model = null
                if (vm.$refs.start) vm.$refs.start.model = null
                if (vm.$refs.options) vm.$refs.options.model = []
                if (vm.$refs.subgroups) vm.$refs.subgroups.model = []
                if (vm.$refs.end) vm.$refs.end.model = null
            })
        },
        setDate(date) {
            const vm = this
            if (!date) return
            let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
            let dateSelected = vm.dates.find(d => d.date === dateFormatted)
            if (vm.autoDate) {
                dateSelected = date
                vm.selectDate = date
            }
            vm.$nextTick(() => { if (vm.$refs.dates) vm.$refs.dates.model = dateSelected || null })
        },
        onChangeSetOption(options) {
            const vm = this
            if (vm.copyTo === 'teams') return vm.setCurrentTeam(options)
            if (vm.copyTo === 'groups') return vm.setCurrentGroups(options)
            return vm.setStudents(options)
        },
        setStudents(students) {
            const vm = this
            if (students?.length === 0) return vm.students = students
            let currentStudents = [...students]
            let lastStudent = currentStudents[currentStudents.length - 1]
            currentStudents = currentStudents.filter(st => typeof st !== 'string')

            if (typeof lastStudent === 'string') {
                let foundStudent = vm.studentsOptionsFiltered.find(student => student.name?.toLowerCase() === lastStudent.toLowerCase()) || null
                foundStudent ? vm.students.push(foundStudent) : vm.students = vm.students.filter(st => typeof st !== 'string')
            } else vm.students = students
        },
        setCurrentGroups(groups) {
            const vm = this
            if (groups?.length === 0) return vm.groups = groups
            let currentGroups = [...groups]
            let lastGroup = currentGroups[currentGroups.length - 1]
            currentGroups = currentGroups.filter(gp => typeof gp !== 'string')

            if (typeof lastGroup === 'string') {
                let foundGroup = vm.groupsOptionsFiltered?.find(group => group.name?.toLowerCase() === lastGroup?.toLowerCase()) || null
                foundGroup ? vm.groups.push(foundGroup) : vm.groups = vm.groups.filter(gp => typeof gp !== 'string')
            } else vm.groups = groups
        },
        setCurrentTeam(team) {
            const vm = this
            if (team?.name) return vm.team = team
            if (team) {
                let foundTeam = vm.teamsOptions?.find(tm => tm.name?.toLowerCase() === team.toLowerCase()) || null
                vm.team = foundTeam || null
            } else vm.team = team
        },
        setCurrentTeamGroup(subgroups) {
            const vm = this
            if (subgroups?.length === 0) return vm.subgroups = subgroups
            let currentSubgroups = [...subgroups]
            let lastSubgroup = currentSubgroups[currentSubgroups.length - 1]
            currentSubgroups = currentSubgroups.filter(sbg => typeof sbg !== 'string')

            if (typeof lastSubgroup === 'string') {
                let foundSubgroup = vm.subgroupsTeamOptions?.find(sbg => sbg.name?.toLowerCase() === lastSubgroup?.toLowerCase()) || null
                foundSubgroup ? vm.subgroups.push(foundSubgroup) : vm.subgroups = vm.subgroups.filter(sbg => typeof sbg !== 'string')
            } else vm.subgroups = subgroups
        },
        daysToCopy() {
            const vm = this
            if (!vm.multiple) return vm.startDate ? [vm.startDate] : []

            let currentDay = vm.$moment(vm.startDate)
            const lastDay = vm.$moment(vm.endDate)
            const dates = []
            const days = [...vm.days].sort((a, b) => a.value - b.value)

            while (currentDay.isSameOrBefore(lastDay)) {
                if (days[currentDay.day()].active) dates.push(currentDay.format('YYYY-MM-DD'))
                currentDay.add(1, 'days')
            }

            return dates
        },
        async populateAssignedRoutineInDate() {
            const vm = this

            const range = vm.daysToCopy()
            const pairs = []
            for (const date of range) {
                for (const currentItem of vm.destinyToCopy) {
                    pairs.push({ date, currentItem })
                }
            }

            const results = await Promise.all(pairs.map(({ date, currentItem }) =>
                vm.isGroup
                    ? hasGroupAssignedRoutineInDate(date, currentItem.id, currentItem.teamId, vm.routineNumberDestiny)
                    : hasAssignedRoutineInDate(currentItem.objectID, date, vm.routineNumberDestiny)
            ))

            vm.assignedRoutinesInSameDate = results
                .map((res, i) => {
                    if (!res.has) return null
                    const { date, currentItem } = pairs[i]
                    return {
                        assignedRoutineId: res.id,
                        date: vm.$moment(date).format('DD-MM-YYYY'),
                        assignedRoutinesIds: res.assignedRoutinesIds || null,
                        name: currentItem.name,
                        replace: false,
                    }
                })
                .filter(Boolean)
        },
        async checkAssignRoutines() {
            const vm = this
            if (vm.loading) return
            vm.loading = true
            const operationId = vm.operationId

            if (vm.beforeCopy && vm.copyTo === 'self') await vm.beforeCopy(vm.daysToCopy())
            await vm.populateAssignedRoutineInDate()
            if (operationId !== vm.operationId) return

            if (vm.assignedRoutinesInSameDate.length === 0) return await vm.copyRoutine()
            vm.$refs.replaceRoutinesDates.show()
        },
        async onConfirmReplace({ toReplace }) {
            const vm = this
            const datesToSkip = []
            const operationId = vm.operationId

            await Promise.all(toReplace.map(async routine => {
                if (!routine.replace) {
                    datesToSkip.push({ date: routine.date, name: routine.name })
                    return
                }
                if (routine.assignedRoutinesIds && vm.isGroup) {
                    await deleteAssignedRoutinesGroup(routine.assignedRoutinesIds)
                    await db.collection('groupAssignedRoutines').doc(routine.assignedRoutineId).delete()
                } else {
                    await db.collection('assignedRoutines').doc(routine.assignedRoutineId).delete()
                }
            }))
            if (operationId !== vm.operationId) return

            vm.datesToSkip = datesToSkip
            await vm.copyRoutine()
        },
        async copyRoutine() {
            const vm = this
            const sourceId = vm.selectDate?.assignedRoutineId || null
            const sourceCollection = vm.collectionName
            const copyTo = vm.copyTo
            const routineNumberDestiny = vm.routineNumberDestiny
            const destinies = [...vm.destinyToCopy]
            const range = vm.daysToCopy()
            const dateNameSet = new Set(vm.datesToSkip.map(item => JSON.stringify(item)))

            if (range.length === 0 || destinies.length === 0) {
                reportDiagnostic({
                    title: 'Copiar rutina sin destino',
                    fields: {
                        startDate: JSON.stringify(vm.startDate),
                        endDate: JSON.stringify(vm.endDate),
                        multiple: vm.multiple,
                        autoDate: vm.autoDate,
                        selectDate: JSON.stringify(vm.selectDate),
                        diasActivos: vm.days.filter(day => day.active).map(day => day.value).join(',') || 'ninguno',
                        copyTo,
                        destinos: destinies.length,
                        datesToSkip: vm.datesToSkip.length,
                        routineNumberDestiny,
                    },
                })
                vm.toast(vm.$t('routines.error_coping_routine'), 'error')
                vm.hide()
                return
            }

            await vm.withLoading(async () => {
            let assignedRoutineData = null

            if (sourceId) {
                const assignedRoutineRef = await db.collection(sourceCollection).doc(sourceId).get()
                assignedRoutineData = assignedRoutineRef.data()
            }

            if (!assignedRoutineData) {
                vm.toast(vm.$t('routines.error_coping_routine'), 'error')
                vm.hide()
                return
            }

            const usedPrescriptionIds = [...new Set(
                (assignedRoutineData.blocks || [])
                    .flatMap(block => block?.exercises || [])
                    .filter(exercise => !!exercise?.prescription)
                    .map(exercise => exercise.prescription)
            )];

            let prescriptions = []
            if (usedPrescriptionIds.length > 0) {
                let filter = usedPrescriptionIds.map(id => `objectID:"${id}"`).join(' OR ')
                let response = await PrescriptionIndex.search('', {
                    filters: filter,
                    hitsPerPage: 1000,
                })
                prescriptions = response.hits
            }

            let blocks = clearBlocksAssignedRoutine(assignedRoutineData.blocks)
            if (prescriptions.length > 0) {
                blocks = getBlockNextPrescription(blocks, prescriptions)
            }
            const tasks = []
            for (const date of range) {
                const formattedDate = vm.$moment(date).format('DD-MM-YYYY')
                for (const currentItem of destinies) {
                    if (dateNameSet.has(JSON.stringify({ date: formattedDate, name: currentItem.name }))) continue
                    tasks.push({ date, formattedDate, currentItem })
                }
            }

            const results = await Promise.all(tasks.map(async ({ date, formattedDate, currentItem }) => {
                const payload = getPayloadBaseAssignedRoutine(`${currentItem.name} - ${formattedDate}`, date, assignedRoutineData, blocks, routineNumberDestiny)
                payload.coachId = vm.coachId
                if (currentItem.objectID) {
                    payload.studentId = currentItem.objectID
                } else {
                    payload.groupId = currentItem.id
                    payload.teamId = currentItem.teamId || null
                }
                const collection = currentItem.objectID ? 'assignedRoutines' : 'groupAssignedRoutines'
                const assignedRoutineCreated = await db.collection(collection).add(payload)

                if (collection === 'groupAssignedRoutines') {
                    await Promise.all((currentItem.students || []).map(student =>
                        deleteAssignedRoutineStudentInDate(student, date, routineNumberDestiny)
                    ))
                    await addAssignedRoutinesGroup({ id: assignedRoutineCreated.id, ...payload, date }, currentItem.students)
                }

                return { payload, date, id: assignedRoutineCreated.id }
            }))

            const assignedRoutines = copyTo === 'self'
                ? results.map(r => ({ ...r.payload, date: r.date, id: r.id }))
                : []

            vm.$emit('success', vm.autoDate ? assignedRoutines[0]?.date : assignedRoutines)
            vm.hide()
            vm.reset()
            })
        },
        datesAllowed(date) {
            const vm = this
            if (vm.copyTo !== 'self') return true
            let dates = vm.filteredDates.map(item => {
                const [day, month, year] = item.date.split('-')
                return `${year}-${month}-${day}`
            })
            return !dates.includes(date)
        },
    },

}
</script>

<style scoped>
.label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.toggle-row-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.toggle-row-text .label {
    margin-bottom: 2px;
}

.toggle-hint {
    font-size: 11px;
    line-height: 15px;
    color: var(--v-txtMuted-base);
}

.toggle-label {
    cursor: pointer;
    width: fit-content;
}

.day-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.day-pill {
    min-width: 42px;
    height: 42px;
    padding: 0 8px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
    color: var(--v-txtSecondary-base);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .15s;
}

.day-pill:hover {
    border-color: var(--v-outline2-base);
}

.day-pill.active {
    background: var(--v-accent-base);
    border-color: var(--v-accent-base);
    color: #0C0E12;
}
</style>